import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Server,
  ScrollText,
  Key,
  ShieldAlert,
  Flag,
  Activity,
  Globe,
  Coins,
  FileText,
  RefreshCw,
} from 'lucide-react';
import { useState } from 'react';

import type {
  ApiKey,
  Country,
  Currency,
  Locale,
  PlatformService,
  RolloutFlag,
  SecurityEvent,
  ServiceEvent,
  TaxRule,
  UpstreamHealthCheck,
} from '@terra/api-client';
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
} from '@terra/ui';

import { api } from '../lib/api';

function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: string[];
  active: string;
  onChange: (t: string) => void;
}) {
  return (
    <div className="border-border/60 bg-surface-subtle/30 mb-6 flex flex-wrap items-center gap-2 rounded-2xl border p-2 shadow-sm">
      {tabs.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => onChange(t)}
          className={`flex-1 rounded-xl px-4 py-2.5 text-[13px] font-bold transition-all sm:flex-none ${
            active === t
              ? 'bg-surface text-ink border-border/60 border shadow-sm'
              : 'text-ink-muted hover:bg-surface-subtle/50 hover:text-ink border border-transparent'
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// PLATFORM  (Phase 3.5 — service registry, durable event log)
// ══════════════════════════════════════════════════════════════════

const SERVICE_STATUS_TONE: Record<string, BadgeTone> = {
  HEALTHY: 'success',
  DEGRADED: 'warning',
  DOWN: 'danger',
};
const EVENT_STATUS_TONE: Record<string, BadgeTone> = {
  QUEUED: 'warning',
  DELIVERED: 'neutral',
  PROCESSING: 'warning',
  COMPLETED: 'success',
  FAILED: 'danger',
  SKIPPED: 'neutral',
};

function ServicesTab() {
  const services = useQuery({
    queryKey: ['platform-services'],
    queryFn: () => api.platform.listServices(),
  });

  if (services.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  if ((services.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-12 shadow-sm">
        <EmptyState
          icon={<Server className="text-ink-muted h-10 w-10" />}
          title="No services"
          description="Registered services will appear here."
        />
      </div>
    );

  return (
    <div className="space-y-4">
      {(services.data ?? []).map((s: PlatformService) => (
        <div
          key={s.id}
          className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm transition-all hover:shadow-md"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-surface-subtle rounded-xl p-2.5">
                <Server className="text-ink-subtle h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <span className="text-ink font-mono text-[14px] font-bold">{s.key}</span>
                  <Badge tone="neutral">{s.tier}</Badge>
                </div>
                <div className="text-ink-muted mt-0.5 text-[13px] font-medium">{s.name}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {s.version && (
                <span className="text-ink-subtle text-[12px] font-bold">v{s.version}</span>
              )}
              <span className="text-ink-subtle flex items-center gap-1 text-[12px] font-medium">
                <Activity className="h-3 w-3" />
                {s.lastHeartbeatAt
                  ? new Date(s.lastHeartbeatAt).toLocaleTimeString()
                  : 'no heartbeat'}
              </span>
              <Badge tone={SERVICE_STATUS_TONE[s.status] ?? 'neutral'}>{s.status}</Badge>
            </div>
          </div>
          <div className="border-border/40 mt-5 grid gap-4 border-t pt-4 text-[13px] sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-ink-muted mb-1 text-[12px] font-medium">Owns</p>
              <p className="text-ink font-semibold">{s.ownedTables.join(', ') || '—'}</p>
            </div>
            <div>
              <p className="text-ink-muted mb-1 text-[12px] font-medium">Endpoints</p>
              <p className="text-ink font-semibold">{s.endpointPrefixes.join(', ') || '—'}</p>
            </div>
            <div>
              <p className="text-ink-muted mb-1 text-[12px] font-medium">Emits</p>
              <p className="text-ink font-semibold">{s.emitsEvents.join(', ') || '—'}</p>
            </div>
            <div>
              <p className="text-ink-muted mb-1 text-[12px] font-medium">Consumes</p>
              <p className="text-ink font-semibold">{s.consumesEvents.join(', ') || '—'}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EventsTab() {
  const events = useQuery({
    queryKey: ['platform-events'],
    queryFn: () => api.platform.listEvents({ limit: 100 }),
  });

  if (events.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  if ((events.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-12 shadow-sm">
        <EmptyState
          icon={<ScrollText className="text-ink-muted h-10 w-10" />}
          title="No events"
          description="The durable event log is empty."
        />
      </div>
    );

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
              <th className="px-4 py-3 pl-5">When</th>
              <th className="px-4 py-3">Kind</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Subject</th>
              <th className="px-4 py-3 text-center">Attempts</th>
              <th className="px-4 py-3 pr-5 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-border/40 divide-y text-[13px]">
            {(events.data ?? []).map((e: ServiceEvent) => (
              <tr key={e.id} className="hover:bg-surface-subtle/30 group transition-colors">
                <td className="px-4 py-3 pl-5 text-[12px] font-medium">
                  {new Date(e.occurredAt).toLocaleString()}
                </td>
                <td className="text-ink px-4 py-3 font-mono text-[12px] font-bold">{e.kind}</td>
                <td className="px-4 py-3 font-semibold">{e.sourceService}</td>
                <td className="px-4 py-3 font-mono text-[12px] font-semibold">
                  {e.subjectId.slice(0, 14)}
                </td>
                <td className="px-4 py-3 text-center font-bold">{e.attempts}</td>
                <td className="px-4 py-3 pr-5 text-center">
                  <Badge tone={EVENT_STATUS_TONE[e.status] ?? 'neutral'}>{e.status}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PlatformPage() {
  const [tab, setTab] = useState('Services');
  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">Platform</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Service registry, heartbeats and the durable cross-service event log.
          </p>
        </div>
      </div>
      <Tabs tabs={['Services', 'Event log']} active={tab} onChange={setTab} />
      {tab === 'Services' && <ServicesTab />}
      {tab === 'Event log' && <EventsTab />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// SCALE  (Phase 3.6 — API keys, security events, flags, upstreams)
// ══════════════════════════════════════════════════════════════════

const KEY_STATUS_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'success',
  REVOKED: 'danger',
  EXPIRED: 'neutral',
};
const SEVERITY_TONE: Record<string, BadgeTone> = {
  INFO: 'neutral',
  LOW: 'neutral',
  MEDIUM: 'warning',
  HIGH: 'danger',
  CRITICAL: 'danger',
};
const FLAG_STATUS_TONE: Record<string, BadgeTone> = {
  DISABLED: 'neutral',
  ROLLOUT: 'warning',
  ENABLED: 'success',
  ARCHIVED: 'neutral',
};
const UPSTREAM_TONE: Record<string, BadgeTone> = {
  GREEN: 'success',
  YELLOW: 'warning',
  RED: 'danger',
  CIRCUIT_OPEN: 'danger',
};

function ApiKeysTab() {
  const qc = useQueryClient();
  const keys = useQuery({ queryKey: ['scale-keys'], queryFn: () => api.scale.listApiKeys() });
  const [form, setForm] = useState({ name: '', scopes: '' });
  const [issued, setIssued] = useState<string | null>(null);

  const issue = useMutation({
    mutationFn: () =>
      api.scale.issueApiKey({
        name: form.name.trim(),
        scopes: form.scopes
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      }),
    onSuccess: (k) => {
      setIssued(k.plaintext);
      setForm({ name: '', scopes: '' });
      void qc.invalidateQueries({ queryKey: ['scale-keys'] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.scale.revokeApiKey(id, 'Revoked by admin'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['scale-keys'] }),
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm">
        <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
          <Key className="h-4 w-4" /> Issue API Key
        </h3>
        <form
          className="grid gap-5 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (form.name && form.scopes) issue.mutate();
          }}
        >
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <Input
            label="Scopes"
            value={form.scopes}
            onChange={(e) => setForm((f) => ({ ...f, scopes: e.target.value }))}
            placeholder="e.g. orders.read, catalog.read"
            required
          />
          <div className="border-border/40 mt-2 flex items-center justify-end border-t pt-4 sm:col-span-2">
            <Button type="submit" loading={issue.isPending} className="px-8">
              Issue Key
            </Button>
          </div>
        </form>
        {issued && (
          <div className="mt-5 rounded-xl border border-amber-300 bg-amber-50 p-4">
            <p className="mb-2 text-[13px] font-bold text-amber-800">
              Copy this key now — it is shown only once:
            </p>
            <div className="break-all rounded-lg bg-amber-100/50 p-3 font-mono text-[14px] font-bold text-amber-900">
              {issued}
            </div>
          </div>
        )}
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {keys.isLoading ? (
          <div className="grid min-h-[300px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : (keys.data ?? []).length === 0 ? (
          <div className="py-12">
            <EmptyState
              icon={<Key className="text-ink-muted h-10 w-10" />}
              title="No API keys"
              description="Issue your first key above."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-4 py-3 pl-5">Name</th>
                  <th className="px-4 py-3">Prefix</th>
                  <th className="px-4 py-3 text-center">Tier</th>
                  <th className="px-4 py-3 text-center">Last Used</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 pr-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {(keys.data ?? []).map((k: ApiKey) => (
                  <tr key={k.id} className="hover:bg-surface-subtle/30 group transition-colors">
                    <td className="text-ink px-4 py-3 pl-5 font-bold">{k.name}</td>
                    <td className="text-ink px-4 py-3 font-mono text-[12px] font-semibold">
                      {k.prefix}…
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge tone="neutral">{k.rateLimitTier}</Badge>
                    </td>
                    <td className="px-4 py-3 text-center text-[12px] font-medium">
                      {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge tone={KEY_STATUS_TONE[k.status] ?? 'neutral'}>{k.status}</Badge>
                    </td>
                    <td className="px-4 py-3 pr-5 text-right">
                      {k.status === 'ACTIVE' && (
                        <Button size="sm" variant="outline" onClick={() => revoke.mutate(k.id)}>
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SecurityTab() {
  const events = useQuery({
    queryKey: ['scale-security'],
    queryFn: () => api.scale.listSecurityEvents({ limit: 100 }),
  });

  if (events.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  if ((events.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-12 shadow-sm">
        <EmptyState
          icon={<ShieldAlert className="text-ink-muted h-10 w-10" />}
          title="No security events"
          description="Security events will appear here."
        />
      </div>
    );

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
              <th className="px-4 py-3 pl-5">When</th>
              <th className="px-4 py-3">Kind</th>
              <th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3 text-center">IP</th>
              <th className="px-4 py-3 pr-5 text-center">Severity</th>
            </tr>
          </thead>
          <tbody className="divide-border/40 divide-y text-[13px]">
            {(events.data ?? []).map((e: SecurityEvent) => (
              <tr key={e.id} className="hover:bg-surface-subtle/30 group transition-colors">
                <td className="px-4 py-3 pl-5 text-[12px] font-medium">
                  {new Date(e.createdAt).toLocaleString()}
                </td>
                <td className="text-ink px-4 py-3 font-semibold">{e.kind.replace(/_/g, ' ')}</td>
                <td className="px-4 py-3 font-medium">
                  <span className="text-ink font-semibold">{e.actorType}</span>
                  {e.actorId ? (
                    <span className="text-ink-muted ml-2 font-mono text-[12px]">
                      {e.actorId.slice(0, 10)}
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-center font-mono text-[12px] font-semibold">
                  {e.ip ?? '—'}
                </td>
                <td className="px-4 py-3 pr-5 text-center">
                  <Badge tone={SEVERITY_TONE[e.severity] ?? 'neutral'}>{e.severity}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FlagsTab() {
  const qc = useQueryClient();
  const flags = useQuery({ queryKey: ['scale-flags'], queryFn: () => api.scale.listFlags() });
  const [form, setForm] = useState({ key: '', description: '', rolloutPct: '0' });

  const upsert = useMutation({
    mutationFn: () =>
      api.scale.upsertFlag({
        key: form.key.trim(),
        ...(form.description ? { description: form.description } : {}),
        rolloutBps: Math.round(Number(form.rolloutPct) * 100),
      }),
    onSuccess: () => {
      setForm({ key: '', description: '', rolloutPct: '0' });
      void qc.invalidateQueries({ queryKey: ['scale-flags'] });
    },
  });
  const setStatus = useMutation({
    mutationFn: ({ key, status }: { key: string; status: RolloutFlag['status'] }) =>
      api.scale.upsertFlag({ key, status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['scale-flags'] }),
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm">
        <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
          <Flag className="h-4 w-4" /> Upsert Rollout Flag
        </h3>
        <form
          className="grid gap-5 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (form.key) upsert.mutate();
          }}
        >
          <Input
            label="Key"
            value={form.key}
            onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
            placeholder="e.g. new_checkout_flow"
            required
          />
          <Input
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <Input
            label="Rollout (%)"
            type="number"
            min="0"
            max="100"
            value={form.rolloutPct}
            onChange={(e) => setForm((f) => ({ ...f, rolloutPct: e.target.value }))}
          />
          <div className="border-border/40 mt-2 flex items-center justify-end border-t pt-4 sm:col-span-3">
            <Button type="submit" loading={upsert.isPending} className="px-8">
              Save Flag
            </Button>
          </div>
        </form>
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {flags.isLoading ? (
          <div className="grid min-h-[300px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : (flags.data ?? []).length === 0 ? (
          <div className="py-12">
            <EmptyState
              icon={<Flag className="text-ink-muted h-10 w-10" />}
              title="No flags"
              description="Create your first rollout flag above."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-4 py-3 pl-5">Key</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3 text-center">Rollout</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 pr-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {(flags.data ?? []).map((f: RolloutFlag) => (
                  <tr key={f.id} className="hover:bg-surface-subtle/30 group transition-colors">
                    <td className="text-ink px-4 py-3 pl-5 font-mono text-[12px] font-semibold">
                      {f.key}
                    </td>
                    <td className="text-ink-muted px-4 py-3 font-medium">{f.description ?? '—'}</td>
                    <td className="text-ink px-4 py-3 text-center font-bold">
                      {(f.rolloutBps / 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge tone={FLAG_STATUS_TONE[f.status] ?? 'neutral'}>{f.status}</Badge>
                    </td>
                    <td className="px-4 py-3 pr-5 text-right">
                      <div className="flex justify-end gap-2">
                        {f.status !== 'ENABLED' && (
                          <Button
                            size="sm"
                            onClick={() => setStatus.mutate({ key: f.key, status: 'ENABLED' })}
                          >
                            Enable
                          </Button>
                        )}
                        {f.status !== 'DISABLED' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setStatus.mutate({ key: f.key, status: 'DISABLED' })}
                          >
                            Disable
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function UpstreamsTab() {
  const upstreams = useQuery({
    queryKey: ['scale-upstreams'],
    queryFn: () => api.scale.listUpstreams(),
  });

  if (upstreams.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  if ((upstreams.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-12 shadow-sm">
        <EmptyState
          icon={<Activity className="text-ink-muted h-10 w-10" />}
          title="No upstreams"
          description="Upstream health checks will appear here."
        />
      </div>
    );

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {(upstreams.data ?? []).map((u: UpstreamHealthCheck) => (
        <div
          key={u.id}
          className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm transition-all hover:shadow-md"
        >
          <div className="mb-4 flex items-center justify-between">
            <span className="text-ink font-mono text-[14px] font-bold">{u.provider}</span>
            <Badge tone={UPSTREAM_TONE[u.grade] ?? 'neutral'}>{u.grade.replace(/_/g, ' ')}</Badge>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-ink-muted font-medium">Latency</span>
              <span className="text-ink font-semibold">
                {u.latencyMs != null ? `${u.latencyMs} ms` : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-ink-muted font-medium">Failures</span>
              <span className="text-ink font-semibold">
                {u.failuresInWindow}/{u.totalInWindow}
              </span>
            </div>
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-ink-muted font-medium">Checked</span>
              <span className="text-ink font-semibold">
                {new Date(u.lastCheckedAt).toLocaleTimeString()}
              </span>
            </div>
          </div>
          {u.lastError && (
            <div className="mt-4 rounded-lg bg-red-50 p-2.5 text-[12px] font-medium leading-relaxed text-red-600">
              {u.lastError}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function ScalePage() {
  const [tab, setTab] = useState('API keys');
  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">Scale & Security</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            API keys, security events, feature-rollout flags and upstream health.
          </p>
        </div>
      </div>
      <Tabs tabs={['API keys', 'Security', 'Flags', 'Upstreams']} active={tab} onChange={setTab} />
      {tab === 'API keys' && <ApiKeysTab />}
      {tab === 'Security' && <SecurityTab />}
      {tab === 'Flags' && <FlagsTab />}
      {tab === 'Upstreams' && <UpstreamsTab />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// GLOBALIZATION  (Phase 3.7 — countries, currencies, locales, tax)
// ══════════════════════════════════════════════════════════════════

const COUNTRY_STATUS_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'success',
  COMING_SOON: 'warning',
  DISABLED: 'neutral',
};

function CountriesTab() {
  const countries = useQuery({
    queryKey: ['glob-countries'],
    queryFn: () => api.globalization.listCountries(),
  });
  const currencies = useQuery({
    queryKey: ['glob-currencies'],
    queryFn: () => api.globalization.listCurrencies(),
  });
  const locales = useQuery({
    queryKey: ['glob-locales'],
    queryFn: () => api.globalization.listLocales(),
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 flex items-center gap-2 border-b p-4">
          <Globe className="text-ink-muted h-4 w-4" />
          <h3 className="text-ink-muted text-[12px] font-bold uppercase tracking-wider">
            Countries
          </h3>
        </div>
        {countries.isLoading ? (
          <div className="grid min-h-[200px] place-items-center">
            <Spinner size="sm" />
          </div>
        ) : (countries.data ?? []).length === 0 ? (
          <div className="text-ink-muted py-8 text-center text-[13px] font-medium">
            No countries.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-4 py-3 pl-5">Code</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 text-center">Currency</th>
                  <th className="px-4 py-3 text-right">VAT</th>
                  <th className="px-4 py-3 text-center">Default Locale</th>
                  <th className="px-4 py-3 pr-5 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {(countries.data ?? []).map((c: Country) => (
                  <tr key={c.id} className="hover:bg-surface-subtle/30 group transition-colors">
                    <td className="text-ink px-4 py-3 pl-5 font-mono text-[12px] font-bold">
                      {c.code}
                    </td>
                    <td className="text-ink px-4 py-3 font-semibold">{c.name}</td>
                    <td className="px-4 py-3 text-center font-mono text-[12px] font-medium">
                      {c.currencyCode}
                    </td>
                    <td className="text-ink px-4 py-3 text-right font-bold">
                      {(c.vatRateBps / 100).toFixed(2)}%
                    </td>
                    <td className="px-4 py-3 text-center font-medium">{c.defaultLocale}</td>
                    <td className="px-4 py-3 pr-5 text-center">
                      <Badge tone={COUNTRY_STATUS_TONE[c.status] ?? 'neutral'}>
                        {c.status.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
          <div className="border-border/60 flex items-center gap-2 border-b p-4">
            <Coins className="text-ink-muted h-4 w-4" />
            <h3 className="text-ink-muted text-[12px] font-bold uppercase tracking-wider">
              Currencies
            </h3>
          </div>
          {currencies.isLoading ? (
            <div className="grid min-h-[200px] place-items-center">
              <Spinner size="sm" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="px-4 py-3 pl-5">Code</th>
                    <th className="px-4 py-3 text-center">Symbol</th>
                    <th className="px-4 py-3 text-right">Rate→INR</th>
                    <th className="px-4 py-3 pr-5 text-center">Active</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {(currencies.data ?? []).map((c: Currency) => (
                    <tr key={c.id} className="hover:bg-surface-subtle/30 group transition-colors">
                      <td className="text-ink px-4 py-3 pl-5 font-mono text-[12px] font-bold">
                        {c.code}
                      </td>
                      <td className="px-4 py-3 text-center font-bold">{c.symbol}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-600">
                        {(c.rateToInrX10000 / 10000).toFixed(4)}
                      </td>
                      <td className="px-4 py-3 pr-5 text-center">
                        {c.active ? (
                          <Badge tone="success">Yes</Badge>
                        ) : (
                          <Badge tone="neutral">No</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
          <div className="border-border/60 flex items-center gap-2 border-b p-4">
            <FileText className="text-ink-muted h-4 w-4" />
            <h3 className="text-ink-muted text-[12px] font-bold uppercase tracking-wider">
              Locales
            </h3>
          </div>
          {locales.isLoading ? (
            <div className="grid min-h-[200px] place-items-center">
              <Spinner size="sm" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="px-4 py-3 pl-5">Code</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Native</th>
                    <th className="px-4 py-3 pr-5 text-center">RTL</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {(locales.data ?? []).map((l: Locale) => (
                    <tr key={l.id} className="hover:bg-surface-subtle/30 group transition-colors">
                      <td className="text-ink px-4 py-3 pl-5 font-mono text-[12px] font-bold">
                        {l.code}
                      </td>
                      <td className="text-ink px-4 py-3 font-semibold">{l.name}</td>
                      <td className="text-ink-muted px-4 py-3 font-medium">
                        {l.nativeName ?? '—'}
                      </td>
                      <td className="text-ink px-4 py-3 pr-5 text-center font-bold">
                        {l.rtl ? 'Yes' : 'No'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TaxRulesTab() {
  const rules = useQuery({
    queryKey: ['glob-tax'],
    queryFn: () => api.globalization.listTaxRules(),
  });

  if (rules.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  if ((rules.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-12 shadow-sm">
        <EmptyState
          icon={<FileText className="text-ink-muted h-10 w-10" />}
          title="No tax rules"
          description="Tax rules will appear here."
        />
      </div>
    );

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
              <th className="px-4 py-3 pl-5">Country</th>
              <th className="px-4 py-3">Kind</th>
              <th className="px-4 py-3 text-center">HSN/SAC</th>
              <th className="px-4 py-3 text-right">Rate</th>
              <th className="px-4 py-3 text-center">Inclusive</th>
              <th className="px-4 py-3">Label</th>
              <th className="px-4 py-3 pr-5 text-center">Active</th>
            </tr>
          </thead>
          <tbody className="divide-border/40 divide-y text-[13px]">
            {(rules.data ?? []).map((t: TaxRule) => (
              <tr key={t.id} className="hover:bg-surface-subtle/30 group transition-colors">
                <td className="text-ink px-4 py-3 pl-5 font-mono text-[12px] font-bold">
                  {t.countryCode}
                </td>
                <td className="px-4 py-3">
                  <Badge tone="neutral">{t.kind}</Badge>
                </td>
                <td className="px-4 py-3 text-center font-mono text-[12px] font-medium">
                  {t.hsnSac ?? '—'}
                </td>
                <td className="px-4 py-3 text-right font-bold text-emerald-600">
                  {(t.rateBps / 100).toFixed(2)}%
                </td>
                <td className="text-ink px-4 py-3 text-center font-bold">
                  {t.inclusive ? 'Yes' : 'No'}
                </td>
                <td className="text-ink-muted px-4 py-3 font-medium">{t.label ?? '—'}</td>
                <td className="px-4 py-3 pr-5 text-center">
                  {t.active ? <Badge tone="success">Yes</Badge> : <Badge tone="neutral">No</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FxToolTab() {
  const [form, setForm] = useState({ amount: '', from: 'USD', to: 'INR' });
  const convert = useMutation({
    mutationFn: () =>
      api.globalization.convert({
        amountMinor: Math.round(Number(form.amount) * 100),
        fromCurrency: form.from.toUpperCase(),
        toCurrency: form.to.toUpperCase(),
      }),
  });

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm">
      <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
        <RefreshCw className="h-4 w-4" /> Currency Converter
      </h3>
      <form
        className="grid gap-5 sm:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (Number(form.amount) > 0) convert.mutate();
        }}
      >
        <Input
          label="Amount"
          type="number"
          min="0"
          step="0.01"
          value={form.amount}
          onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
          required
        />
        <Input
          label="From"
          value={form.from}
          onChange={(e) => setForm((f) => ({ ...f, from: e.target.value.toUpperCase() }))}
          placeholder="e.g. USD"
          required
        />
        <Input
          label="To"
          value={form.to}
          onChange={(e) => setForm((f) => ({ ...f, to: e.target.value.toUpperCase() }))}
          placeholder="e.g. INR"
          required
        />
        <div className="border-border/40 mt-2 flex items-center justify-end border-t pt-4 sm:col-span-4">
          <Button type="submit" loading={convert.isPending} className="px-8">
            Convert
          </Button>
        </div>
      </form>
      {convert.data && (
        <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3">
            <span className="text-xl font-bold tracking-tight text-emerald-900">
              {(convert.data.amountMinor / 100).toFixed(2)} {convert.data.fromCurrency} ={' '}
              {(convert.data.convertedMinor / 100).toFixed(2)} {convert.data.toCurrency}
            </span>
            <span className="text-[13px] font-medium text-emerald-700">
              @ {(convert.data.rateX10000 / 10000).toFixed(4)} rate
            </span>
          </div>
        </div>
      )}
      {convert.isError && (
        <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-[13px] font-bold text-red-700">
            Conversion failed — check currency codes.
          </p>
        </div>
      )}
    </div>
  );
}

export function GlobalizationPage() {
  const [tab, setTab] = useState('Registries');
  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">Globalization</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Countries, currencies, locales, tax rules and FX conversion.
          </p>
        </div>
      </div>
      <Tabs tabs={['Registries', 'Tax rules', 'FX tool']} active={tab} onChange={setTab} />
      {tab === 'Registries' && <CountriesTab />}
      {tab === 'Tax rules' && <TaxRulesTab />}
      {tab === 'FX tool' && <FxToolTab />}
    </div>
  );
}
